import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import userPlaceholder from '../images/user.svg';
import { userProfileService } from '../services/userProfileService';
import userService from '../services/userService';
import {
    appendQueryParam,
    clearAvatarCache,
    DEFAULT_AVATAR_CACHE_TTL_MS,
    isAvatarCacheFresh,
    readAvatarCache,
    warmAvatarImageCache,
    writeAvatarCache,
} from '../utils/avatarCache';
import { emitAvatarUpdate, subscribeAvatarUpdate } from '../utils/avatarEvents';

const inFlightRequests = new Map();
const usernameResolutionCache = new Map();
const userMetaCache = new Map();

const normalizeAudience = (audience) => (
    audience === 'public' ? 'public' : 'viewer'
);

const normalizeRoles = (roles) => {
    if (!roles) {
        return [];
    }
    if (Array.isArray(roles)) {
        return roles;
    }
    try {
        return Array.from(roles);
    } catch (error) {
        return [];
    }
};

const normalizeUserId = (userId) => {
    if (userId === null || userId === undefined) {
        return null;
    }
    const trimmed = String(userId).trim();
    if (!trimmed || trimmed.toLowerCase() === 'undefined') {
        return null;
    }
    return trimmed;
};

const normalizeUsername = (username) => {
    if (!username) {
        return null;
    }
    const trimmed = String(username).trim();
    return trimmed || null;
};

const fetchAvatarOnce = (userId, audience = 'viewer') => {
    if (!userId) {
        return Promise.resolve({ url: null });
    }
    const normalizedAudience = normalizeAudience(audience);
    const requestKey = `${normalizedAudience}:${userId}`;
    if (!inFlightRequests.has(requestKey)) {
        const request = Promise.resolve()
            .then(() => (normalizedAudience === 'public'
                ? userProfileService.getPublicProfilePictureUrl(userId)
                : userProfileService.getProfilePictureUrl(userId)))
            .catch((error) => {
                throw error;
            })
            .finally(() => {
                inFlightRequests.delete(requestKey);
            });
        inFlightRequests.set(requestKey, request);
    }
    return inFlightRequests.get(requestKey);
};

const resolveUserIdForUsername = async (username) => {
    const normalized = normalizeUsername(username);
    if (!normalized) {
        return null;
    }
    const cacheKey = normalized.toLowerCase();
    if (usernameResolutionCache.has(cacheKey)) {
        return usernameResolutionCache.get(cacheKey);
    }

    try {
        const user = await userService.fetchUserByUsername(normalized);
        const resolvedId = normalizeUserId(user?.id);
        if (resolvedId) {
            const meta = {
                id: resolvedId,
                roles: normalizeRoles(user.roles),
                username: user.username ?? normalized,
            };
            usernameResolutionCache.set(cacheKey, meta);
            userMetaCache.set(meta.id, meta);
            return meta;
        }
        usernameResolutionCache.set(cacheKey, null);
        return null;
    } catch (error) {
        console.error('Failed to resolve user id for username:', username, error);
        usernameResolutionCache.set(cacheKey, null);
        return null;
    }
};

/**
 * Hook for managing user avatar URLs with unified state and cache-busting support.
 * 
 * This hook provides a single source of truth for avatar URLs across the app.
 * Both the Home page avatar and nav panel avatar use this hook (directly or via useProfilePicture),
 * ensuring they stay in sync through event-based updates.
 * 
 * Cache-busting: When an avatar is uploaded, even if the backend returns the same URL,
 * we increment a version counter and append it as a query parameter (e.g., ?v=1).
 * This forces the browser to fetch the new image instead of using a cached version.
 * The version is only appended when > 0, so initial loads don't have unnecessary parameters.
 * 
 * @param {string} userId - The user ID
 * @param {Object} options - Options including username and cache TTL
 * @returns {Object} Avatar URL, raw URL, setters, and metadata
 */
export const useUserAvatar = (
    userId,
    { username, ttlMs = DEFAULT_AVATAR_CACHE_TTL_MS, enabled = true, audience = 'viewer' } = {}
) => {
    const normalizedUserId = useMemo(() => normalizeUserId(userId), [userId]);
    const normalizedUsername = useMemo(() => normalizeUsername(username), [username]);
    const normalizedAudience = useMemo(() => normalizeAudience(audience), [audience]);
    const latestUserIdRef = useRef(null);
    const latestUsernameRef = useRef(null);
    const resolutionRequestId = useRef(0);
    latestUserIdRef.current = normalizedUserId;
    latestUsernameRef.current = normalizedUsername;
    const [resolvedUserId, setResolvedUserId] = useState(() => normalizedUserId);
    const [userMeta, setUserMeta] = useState(() =>
        (normalizedUserId ? userMetaCache.get(normalizedUserId) || null : null)
    );
    // Use a single canonical key for cache + events to keep avatars in sync.
    const avatarUserId = useMemo(() => {
        const keySource = normalizedUserId || resolvedUserId;
        return keySource ? String(keySource) : null;
    }, [normalizedUserId, resolvedUserId]);
    // Public avatar reads must never reuse authenticated protected-media URLs
    // from session cache, so public mode uses a separate cache/event namespace.
    const avatarCacheKey = useMemo(() => {
        if (!avatarUserId) {
            return null;
        }
        return normalizedAudience === 'public' ? `public:${avatarUserId}` : avatarUserId;
    }, [avatarUserId, normalizedAudience]);
    const initialCache = useMemo(() => (avatarCacheKey ? readAvatarCache(avatarCacheKey) : null), [avatarCacheKey]);
    const [rawUrl, setRawUrl] = useState(() => initialCache?.url || null);
    // Version counter for cache-busting when URL doesn't change but content does
    const [avatarVersion, setAvatarVersion] = useState(() => initialCache?.version ?? 0);

    useEffect(() => {
        let cancelled = false;
        const requestId = ++resolutionRequestId.current;

        const applyResolution = (id, meta) => {
            if (cancelled || resolutionRequestId.current !== requestId) {
                return;
            }
            const resolvedId = normalizeUserId(id);
            setResolvedUserId(resolvedId);
            if (meta && resolvedId) {
                const normalizedMeta = meta.id === resolvedId ? meta : { ...meta, id: resolvedId };
                userMetaCache.set(resolvedId, normalizedMeta);
                setUserMeta(normalizedMeta);
            } else if (resolvedId && userMetaCache.has(resolvedId)) {
                setUserMeta(userMetaCache.get(resolvedId));
            } else {
                setUserMeta(null);
            }
        };

        const resolveId = async () => {
            if (normalizedUserId) {
                const cachedMeta = userMetaCache.get(normalizedUserId) || null;
                applyResolution(normalizedUserId, cachedMeta);
                return;
            }

            if (enabled && normalizedAudience !== 'public' && normalizedUsername) {
                const meta = await resolveUserIdForUsername(normalizedUsername);
                if (cancelled || resolutionRequestId.current !== requestId) {
                    return;
                }
                if (latestUserIdRef.current) {
                    return;
                }
                if (latestUsernameRef.current !== normalizedUsername) {
                    return;
                }
                applyResolution(meta?.id || null, meta);
            } else {
                applyResolution(null, null);
            }
        };

        resolveId();

        return () => {
            cancelled = true;
        };
    }, [enabled, normalizedAudience, normalizedUserId, normalizedUsername]);

    useEffect(() => {
        if (!enabled || !avatarCacheKey) {
            setRawUrl(null);
            setAvatarVersion(0);
            return;
        }
        const cached = readAvatarCache(avatarCacheKey);
        setRawUrl(cached?.url || null);
        setAvatarVersion(cached?.version ?? 0);
    }, [avatarCacheKey, enabled]);

    useEffect(() => {
        if (!enabled || !avatarUserId || normalizedAudience === 'public') {
            setUserMeta(null);
            return;
        }

        if (userMetaCache.has(avatarUserId)) {
            setUserMeta(userMetaCache.get(avatarUserId));
            return;
        }

        let cancelled = false;
        Promise.resolve()
            .then(() => userService.fetchUserById(avatarUserId))
            .then((data) => {
                if (cancelled) {
                    return;
                }
                const resolvedId = normalizeUserId(data?.id);
                if (resolvedId) {
                    const meta = {
                        id: resolvedId,
                        roles: normalizeRoles(data.roles),
                        username: data.username || null,
                    };
                    userMetaCache.set(meta.id, meta);
                    setUserMeta(meta);
                } else {
                    setUserMeta(null);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error('Failed to fetch user metadata:', error);
                    setUserMeta(null);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [avatarUserId, enabled, normalizedAudience]);

    useEffect(() => {
        if (!enabled || !avatarCacheKey) {
            return;
        }
        return subscribeAvatarUpdate(avatarCacheKey, (url, version) => {
            if (url) {
                setRawUrl(url);
                // Fall back to cache increment if the event payload is missing a numeric version.
                const cached = readAvatarCache(avatarCacheKey);
                const newVersion = Number.isFinite(version)
                    ? version
                    : (cached?.version ?? 0) + 1;
                setAvatarVersion(newVersion);
                writeAvatarCache(avatarCacheKey, url, newVersion);
                // Warm cache with versioned URL for proper cache-busting
                warmAvatarImageCache(url, newVersion);
            } else {
                setRawUrl(null);
                setAvatarVersion(0);
                clearAvatarCache(avatarCacheKey);
            }
        });
    }, [avatarCacheKey, enabled]);

    useEffect(() => {
        if (!enabled || !avatarUserId || !avatarCacheKey) {
            return;
        }
        const cached = readAvatarCache(avatarCacheKey);
        if (isAvatarCacheFresh(cached, ttlMs)) {
            return;
        }

        let cancelled = false;
        fetchAvatarOnce(avatarUserId, normalizedAudience)
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const url = response?.url || null;
                if (url) {
                    const currentCache = readAvatarCache(avatarCacheKey);
                    const cachedUrl = currentCache?.url || null;
                    if (cachedUrl && cachedUrl === url) {
                        return;
                    }
                    setRawUrl(url);
                    const newVersion = (currentCache?.version ?? 0) + 1;
                    setAvatarVersion(newVersion);
                    writeAvatarCache(avatarCacheKey, url, newVersion);
                    // Warm cache with versioned URL for proper cache-busting
                    warmAvatarImageCache(url, newVersion);
                    emitAvatarUpdate(avatarCacheKey, url, newVersion);
                } else {
                    setRawUrl(null);
                    setAvatarVersion(0);
                    clearAvatarCache(avatarCacheKey);
                    emitAvatarUpdate(avatarCacheKey, null, 0);
                }
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                console.error('Failed to fetch avatar', error);
            });

        return () => {
            cancelled = true;
        };
    }, [avatarUserId, avatarCacheKey, enabled, normalizedAudience, ttlMs]);

    const setAvatarUrl = useCallback(
        (url) => {
            if (!avatarCacheKey) {
                if (url) {
                    setRawUrl(url);
                    setAvatarVersion((prev) => prev + 1);
                } else {
                    setRawUrl(null);
                    setAvatarVersion(0);
                }
                return;
            }
            if (url) {
                // Increment version to bust browser cache even if URL is the same
                // Update both states separately to ensure React properly tracks changes
                setRawUrl(url);
                setAvatarVersion((prev) => {
                    const newVersion = prev + 1;
                    writeAvatarCache(avatarCacheKey, url, newVersion);
                    // Warm cache with versioned URL for proper cache-busting
                    warmAvatarImageCache(url, newVersion);
                    emitAvatarUpdate(avatarCacheKey, url, newVersion);
                    return newVersion;
                });
            } else {
                setRawUrl(null);
                setAvatarVersion(0);
                clearAvatarCache(avatarCacheKey);
                emitAvatarUpdate(avatarCacheKey, null, 0);
            }
        },
        [avatarCacheKey]
    );

    const refreshAvatar = useCallback(async () => {
        if (!enabled || !avatarUserId || !avatarCacheKey) {
            setRawUrl(null);
            setAvatarVersion(0);
            return null;
        }
        try {
            const response = await fetchAvatarOnce(avatarUserId, normalizedAudience);
            const url = response?.url || null;
            if (url) {
                const cached = readAvatarCache(avatarCacheKey);
                const newVersion = (cached?.version ?? 0) + 1;
                setRawUrl(url);
                setAvatarVersion(newVersion);
                writeAvatarCache(avatarCacheKey, url, newVersion);
                // Warm cache with versioned URL for proper cache-busting
                warmAvatarImageCache(url, newVersion);
                emitAvatarUpdate(avatarCacheKey, url, newVersion);
            } else {
                setRawUrl(null);
                setAvatarVersion(0);
                clearAvatarCache(avatarCacheKey);
                emitAvatarUpdate(avatarCacheKey, null, 0);
            }
            return url;
        } catch (error) {
            throw error;
        }
    }, [avatarUserId, avatarCacheKey, enabled, normalizedAudience]);

    const { placeholder, isModerator } = useMemo(() => {
        const roles = normalizeRoles(userMeta?.roles);
        const moderator = roles.includes('ROLE_MODERATOR');
        return {
            placeholder: moderator ? null : userPlaceholder,
            isModerator: moderator,
        };
    }, [userMeta]);

    const shouldUseSupportAvatar = isModerator && !rawUrl;
    
    // Build cache-busted URL: append version parameter only when we have a URL and version > 0
    // This ensures browser cache is invalidated when avatar content changes but URL stays the same
    const avatarUrl = useMemo(() => {
        if (!rawUrl) {
            return placeholder;
        }
        // Only add cache-buster if version > 0 (meaning avatar has been updated at least once)
        if (avatarVersion > 0) {
            return appendQueryParam(rawUrl, 'v', avatarVersion);
        }
        return rawUrl;
    }, [rawUrl, avatarVersion, placeholder]);

    return {
        avatarUrl,
        rawUrl,
        setAvatarUrl,
        refreshAvatar,
        isModerator,
        shouldUseSupportAvatar,
    };
};
